<template>
  <div
    class="ca-slider-content-card"
    :class="{ 'ca-slider-content-card--loading': isImageLoading }"
    :aria-busy="isImageLoading || undefined"
  >
    <CaSliderCardSkeleton v-if="isImageLoading" variant="content" />
    <div class="ca-slider-content-card__info">
      <span v-if="label" class="ca-slider-content-card__label caption-medium">
        {{ label }}
      </span>
      <component
        :is="normalizeHeadingTag(titleHeadingType, 'p')"
        class="ca-slider-content-card__title heading-bold"
      >
        {{ title }}
      </component>
      <div
        class="ca-slider-content-card__action-wrap ca-slider-content-card__action-wrap--desktop"
      >
        <IconButton
          type="inverted"
          variant="primary"
          class="shadow-2"
          :aria-label="ariaLabel"
          @clicked="$emit('action')"
        >
          <template #trailingIcon>
            <ChevronRight />
          </template>
        </IconButton>
      </div>
    </div>
    <div class="ca-slider-content-card__image">
      <div
        v-if="imageSrc"
        class="ca-slider-content-card__media"
      >
        <img
          :src="imageSrc"
          :alt="imageAlt"
          class="ca-slider-content-card__img"
          @load="handleImageSettled"
          @error="handleImageSettled"
        />
      </div>
      <div
        class="ca-slider-content-card__action-wrap ca-slider-content-card__action-wrap--mobile"
      >
        <IconButton
          type="inverted"
          variant="primary"
          class="shadow-2"
          :aria-label="ariaLabel"
          @clicked="$emit('action')"
        >
          <template #trailingIcon>
            <ChevronRight />
          </template>
        </IconButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import { IconButton } from "ca-online-design-system";
import CaSliderCardSkeleton from "./CaSliderCardSkeleton.vue";
import ChevronRight from "@assets/chevron-acao-principal.svg";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaSliderContentCard",
});



const props = defineProps({
  /** Label text displayed in the badge. */
  label: {
    type: String,
    default: "",
  },
  /** Heading text for the card. */
  title: {
    type: String,
    default: "",
  },
  titleHeadingType: {
    type: String,
    default: "Paragraph",
  },
  /** Image source URL. */
  imageSrc: {
    type: String,
    default: "",
  },
  /** Image alt text. */
  imageAlt: {
    type: String,
    default: "",
  },
  /** Accessible label for the action button. */
  ariaLabel: {
    type: String,
    default: "Ver mais",
  },
});

defineEmits(["action"]);

const isImageLoading = ref(Boolean(props.imageSrc));

watch(
  () => props.imageSrc,
  (imageSrc) => {
    isImageLoading.value = Boolean(imageSrc);
  },
);

function handleImageSettled() {
  isImageLoading.value = false;
}
</script>

<style scoped lang="scss">
.ca-slider-content-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--size-4);
  padding: var(--size-4);
  padding-top: var(--size-3);
  height: 582px;
  width: 100%;
  border-radius: var(--size-5);
  background-color: var(--ca-color-neutrals-on-background-high-40);
  overflow: hidden;

  &--loading {
    > .ca-slider-content-card__info,
    > .ca-slider-content-card__image {
      visibility: hidden;
    }
  }

  @include ca-bp("sm") {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--size-5);
    padding: var(--size-4);
    height: 344px;
  }

  @include ca-bp("md") {
    justify-content: flex-start;
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--size-4);
    width: 100%;
    padding: var(--size-4) var(--size-3);
    flex: 1 1 0;
    min-height: 0;

    @include ca-bp("sm") {
      flex: 0 0 332px;
      width: 332px;
      justify-content: center;
      height: 100%;
    }

    @include ca-bp("md") {
      flex: 1 1 0;
      width: auto;
    }

    @include ca-bp("lg") {
      flex: 0 0 312px;
      width: 312px;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--size-3) var(--size-4);
    border-radius: 100px;
    background-color: var(--ca-color-neutrals-inverted-10);
    color: var(--ca-color-text-inverted);
    white-space: nowrap;
    text-transform: uppercase;
  }

  &__title {
    width: 100%;
    color: var(--ca-color-text-inverted);
    margin: 0;
    font-family: var(--font-family-heading);
    max-height: 96px;
    overflow: hidden;

    @include ca-bp("sm") {
      flex: 1 0 0;
      min-height: 1px;

      max-height: none;
      overflow: visible;
    }
    @include ca-bp("md") {
      font-size: 32px !important;
      line-height: 40px !important;
      letter-spacing: 0.384px !important;
    }
  }

  &__action-wrap {
    flex-shrink: 0;

    :deep(.base-button) {
      width: 48px !important;
      height: 48px !important;
    }

    :deep(svg) {
      transform: translateX(1px);
    }

    &--desktop {
      display: none;

      @include ca-bp("sm") {
        display: block;
      }
    }

    &--mobile {
      display: block;
      position: relative;
      z-index: 1;

      @include ca-bp("sm") {
        display: none;
      }
    }
  }

  &__image {
    position: relative;
    flex: 0 0 360px;
    width: 100%;
    border-radius: var(--size-4);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: flex-end;
    padding: var(--size-4);

    @include ca-bp("sm") {
      flex: 0 0 332px;
      min-height: unset;
      height: 100%;
      width: 332px;
      padding: 0;
    }

    @include ca-bp("md") {
      flex: 1 1 0;
      width: auto;
    }
  }

  &__media {
    position: absolute;
    inset: 0;
    border-radius: var(--size-4);
    overflow: hidden;
  }

  &__img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: var(--size-4);
  }
}
</style>
